import { useMemo, useState } from 'react';
import { Field, Input } from '@/ui/Field';
import { cn } from '@/lib/cn';

/**
 * SelectorDeIcono — elegir un icono de Bootstrap Icons, buscando por nombre.
 *
 * Busca sobre `Utils.listaIconos()`, la misma lista que usa el selector vanilla,
 * así que las pantallas de las dos capas ofrecen exactamente los mismos iconos.
 *
 * Subió de `features/solicitudes/Tipos.tsx` el 2026-09-17, cuando las fechas
 * especiales del calendario lo necesitaron: una feature no importa de otra, y
 * lo compartido vive aquí.
 *
 * Al subir se corrigió una cosa: los botones de la rejilla medían 32 px también
 * con puntero grueso, por debajo de los 44 que exige el portal para lo pulsable.
 */
export function SelectorDeIcono({
  id, valor, onCambio, respaldo = 'bi-file-earmark-text',
}: {
  /** Para la etiqueta: dos selectores en la misma página no pueden compartir id. */
  id: string;
  valor: string;
  onCambio: (v: string) => void;
  /** Qué se enseña en la vista previa mientras no hay ninguno elegido. */
  respaldo?: string;
}) {
  const [q, setQ] = useState('');
  const todos = useMemo(() => Utils.listaIconos(), []);
  // Un emoji de antes del selector. Se enseña tal cual y se avisa, como hacía el
  // selector vanilla: dentro de un `className` no pintaría nada, que es lo que
  // les pasó a las portadas de Formación hasta el 2026-09-21.
  const legado = !!valor && !/^(bi|mdi)-[a-z0-9-]+$/.test(valor);
  const lista = useMemo(() => {
    const f = q.trim().toLowerCase();
    return (f ? todos.filter((n) => n.includes(f)) : todos).slice(0, 60);
  }, [q, todos]);

  return (
    <Field label="Ícono" id={id}>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span className="grid size-9 shrink-0 place-items-center rounded-sm border border-line bg-surface">
            {legado ? (
              <span className="text-4 leading-none" aria-hidden="true">{valor}</span>
            ) : (
              <i className={cn('bi', valor || respaldo, 'text-4 text-brand-ink')} aria-hidden="true" />
            )}
          </span>
          <Input placeholder="Buscar un icono…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {legado && (
          <p className="text-2 text-muted">
            Esta ficha guarda <strong className="text-ink-2">{valor}</strong>, un emoji de antes del
            cambio. Elige un icono para reemplazarlo; si no tocas nada, se queda como está.
          </p>
        )}
        <div className="flex max-h-28 flex-wrap gap-1 overflow-y-auto rounded-sm border border-line bg-surface p-2">
          {lista.map((n) => (
            <button
              key={n}
              type="button"
              title={n}
              aria-label={n}
              aria-pressed={n === valor}
              onClick={() => onCambio(n)}
              className={cn(
                'grid size-8 place-items-center rounded-xs border pointer-coarse:size-11',
                'focus-visible:focus-anillo',
                n === valor ? 'border-brand bg-brand text-on-brand' : 'border-transparent hover:bg-sunken',
              )}
            >
              <i className={cn('bi', n)} aria-hidden="true" />
            </button>
          ))}
          {lista.length === 0 && <p className="text-2 text-muted">Ningún icono con «{q}»</p>}
        </div>
      </div>
    </Field>
  );
}
